import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Endpoint } from '@/Config/api/endpoint';
import { httpClient } from '@/Config/api/httpClient.config';
import { QueryKey } from '@/Config/api/queryKey';
import { ArticleDto, ArticleInputDto } from '@/Domain/api/KnowledgeBase/dto/Article.dto';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

//

type TagObject = { value: string };

export type ArticleForm = ArticleDto & {
  tagsObjects: TagObject[];
};

export interface UseArticleSheetReturn {
  form: ReturnType<typeof useForm<ArticleForm>>;
  article: ReturnType<typeof useQuery<ArticleDto>>;
  isLoading: boolean;
  onSubmit: (values: ArticleForm) => void;
  createSubmitHandler: (options?: {
    onSuccess?: () => void;
    onError?: (error: unknown) => void;
  }) => (values: ArticleForm) => void;
}

export const useArticleSheet = (): UseArticleSheetReturn => {
  const queryClient = useQueryClient();
  const { id } = useParams<{ id?: string }>();
  const { capture } = usePostHogAnalytics();

  // Refs for managing form state
  const skipNextResetRef = useRef(false);

  // Article query
  const article = useQuery<ArticleDto>({
    queryKey: [QueryKey.knowledgeBase.article, id],
    staleTime: 0,
    gcTime: 0,
    enabled: id != null,
    // Disable structural sharing to ensure new reference on refetch,
    // which triggers the form reset effect
    structuralSharing: false,
  });

  // Article mutation
  const articleMutation = useMutation<
    ArticleDto,
    unknown,
    { id: string } & Partial<ArticleInputDto>
  >({
    mutationKey: [QueryKey.knowledgeBase.article, id],
    onSuccess: () => {
      capture(PostHogEventEnum.ArticleUpdated, {
        article_id: id,
      });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.articlesInfinite] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.article, id] });
      void httpClient.post(Endpoint.knowledgeBase.retrain).catch(() => {});
    },
  });

  // Form setup
  const form = useForm<ArticleForm>({
    defaultValues: article.data
      ? {
          ...article.data,
          tagsObjects: (article.data.tags || []).map((t: string) => ({ value: t })),
        }
      : undefined,
  });

  // Track local changes - handled by consumer via form.formState

  // Keep form in sync when article loads/changes
  useEffect(() => {
    if (!article.data) return;
    if (skipNextResetRef.current) {
      skipNextResetRef.current = false;
      return;
    }
    form.reset({
      ...article.data,
      tagsObjects: (article.data.tags || []).map((t: string) => ({ value: t })),
    });
  }, [article.data, form]);

  // Submit handler (base)
  const baseSubmit = useCallback(
    (
      values: ArticleForm,
      callbacks?: { onSuccess?: () => void; onError?: (error: unknown) => void },
    ) => {
      const cleanedTags = (values.tagsObjects || [])
        .map((t) => (t.value ?? '').trim())
        .filter((v) => v.length > 0);

      const {
        id: _id,
        created_timestamp: _created,
        downloaded_timestamp: _downloaded,
        last_updated_timestamp: _lastUpdated,
        last_updated_from_external_source_timestamp: _lastUpdatedExternal,
        content_edited_manually_at: _contentEditedManuallyAt,
        state: _state,
        type: _type,
        tagsObjects: _tagsObjects,
        ...rest
      } = values;

      const payload: Partial<ArticleInputDto> = {
        ...rest,
        tags: cleanedTags,
      };
      // Send content/title only when the user actually edited them: the
      // markdown editor normalizes content on load, so a non-dirty value can
      // differ from the DB row and would read as a manual edit server-side.
      const { dirtyFields } = form.formState;
      if (!dirtyFields.content) delete payload.content;
      if (!dirtyFields.title) delete payload.title;

      skipNextResetRef.current = true;
      if (!id) {
        callbacks?.onError?.(new Error('Missing article id'));
        return;
      }
      articleMutation.mutate(
        { id, ...payload },
        {
          onSuccess: () => {
            const cleanedValues = {
              ...values,
              tagsObjects: cleanedTags.map((v) => ({ value: v })),
            } as typeof values;
            form.reset(cleanedValues);
            callbacks?.onSuccess?.();
          },
          onError: (error) => {
            callbacks?.onError?.(error);
          },
        },
      );
    },
    [articleMutation, form],
  );

  // Public submit variants
  const onSubmit = useCallback((values: ArticleForm) => baseSubmit(values), [baseSubmit]);
  const createSubmitHandler = useCallback(
    (options?: { onSuccess?: () => void; onError?: (error: unknown) => void }) =>
      (values: ArticleForm) =>
        baseSubmit(values, options),
    [baseSubmit],
  );

  const isLoading = useMemo(
    () => article.isLoading || articleMutation.isPending,
    [article.isLoading, articleMutation.isPending],
  );

  return {
    form,
    article,
    isLoading,
    onSubmit,
    createSubmitHandler,
  };
};
